/**
 * How the app paints a structure's unit status.
 *
 * One dot, one word and one chip family per backend `status` token, so the
 * structures grid, the map's structure list and anything else reporting the
 * same token say the same thing in the same colour. A surface that showed its
 * own palette or its own wording for "alarming" would teach an operator a
 * second vocabulary for a fact they already know.
 *
 * Contains:
 * - UnitStatusDot — the coloured dot (default export)
 * - unitStatusLabel — the translated word for a token
 * - unitStatusChipColor — the StatusChip family for a token
 */
import Box from '@mui/material/Box';
import React from 'react';

const DEFAULT_DOT_SIZE = 12;

// Dot fill per backend `status` token. An unrecognized token reads as "no
// unit", the token that claims the least.
const STATUS_COLORS = Object.freeze({
  good: 'var(--status-ok)',
  offline: 'var(--status-warning)',
  bad: 'var(--status-bad)',
  unresolved_maintenance: 'var(--status-attention)',
  no_radio: 'var(--status-neutral)',
});

/**
 * The translated word for one unit-status token.
 *
 * @param {string} status - a backend `status` token.
 * @param {Function} t - the i18next translator.
 * @returns {string}
 */
export function unitStatusLabel(status, t) {
  switch (status) {
    case 'good':
      return t('unitStatus.good');
    case 'offline':
      return t('unitStatus.missedCheckin');
    case 'bad':
      return t('unitStatus.alarming');
    case 'unresolved_maintenance':
      return t('unitStatus.maintenance');
    case 'no_radio':
    default:
      return t('unitStatus.noUnit');
  }
}

/**
 * The StatusChip family for one unit-status token — the same families the dot
 * paints from, so a dot and a pill on the same row always agree.
 *
 * @param {string} status - a backend `status` token.
 * @returns {string} a StatusChip `color`.
 */
export function unitStatusChipColor(status) {
  switch (status) {
    case 'good':
      return 'ok';
    case 'offline':
      return 'warning';
    case 'bad':
      return 'bad';
    case 'unresolved_maintenance':
      return 'attention';
    default:
      return 'neutral';
  }
}

/**
 * @param {object} props
 * @param {string} props.status - a backend `status` token.
 * @param {number} [props.size] - diameter in pixels.
 * @param {string} [props.title] - hover/assistive text, usually `unitStatusLabel`.
 */
export default function UnitStatusDot({ status, size = DEFAULT_DOT_SIZE, title }) {
  return (
    <Box
      title={title}
      aria-label={title}
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        flex: '0 0 auto',
        bgcolor: STATUS_COLORS[status] ?? STATUS_COLORS.no_radio,
      }}
    />
  );
}
